/**
 * 备料台①「拍这条片是为了啥」—— **自由流的目的入口**(2026-09-12 目的锁定 A2 收窄)。
 *
 * ═══ 目的锁定后这一行只剩「选」,没有「换」 ══════════════════════════════════════
 * 老板拍板:目的在 hub 选定,进创建页就锁死(「一开始给 AI 导演的东西更精准」)。
 * 于是:
 *   - **选定了目的** → 页面顶部是「拍摄任务书」横幅(`VideoPurposeBanner`),这一行不出现
 *     (由页面层 `showPurpose && !lockedPurpose` 控制)。横幅里没有换目的的入口。
 *   - **没选目的**(hub「先来一条」自由进 / 直链)→ 这一行给一次选目的的机会;选了就写进
 *     URL `?usage=` 并锁定(横幅接管、本行退场)。所以本行只会以「未选」态出现。
 * 删掉的东西(拍板逐条点名):
 *   - 折叠态的「换目的」链接(`purpose-expand`)—— 换目的整个不允许;
 *   - 「换成 X」换叙事弧链接(`purpose-switch-alt`)—— 带货/硬广是 hub 两张独立卡,
 *     页内换弧 = 换目的,违背锁定;
 *   - 拍法「换一张」搬进横幅(同目的内换打法,不破坏精准性,故保留但归横幅管)。
 *
 * ★仍是 radiogroup(见 OptionChip 头注):选目的是单选组语义,键盘/读屏要能在组内走。
 * ★首位「AI 判断」是真选项不是空态:不选 = 交给 ai `intent_infer` 从文案判(2026-08-02 起)。
 */
import { PURPOSE_GROUPS, purposeGroupOf } from "@/entities/scene-card/model/briefPurpose";
import { Sparkles } from "lucide-react";

import { OptionChip, OptionChipGroup } from "./OptionChip";

interface Props {
  /** 当前目的 label(引擎契约字面量);null = 交给 AI 判。 */
  value: string | null | undefined;
  /** 选目的 / 改回 AI 判(null)。storyMode 映射 + URL 锁定由页面层 applyPurpose 处理。 */
  onChange: (label: string | null) => void;
  disabled?: boolean;
}

export function VideoPurposeRow({ value, onChange, disabled }: Props) {
  // 本行只在「未选目的」时渲染(选了就锁 → 横幅接管);currentGroup 仅用于高亮容错。
  const currentGroup = purposeGroupOf(value);
  const current = !!currentGroup;

  return (
    // 不写 aria-disabled:<section> 的隐式 role=region 不支持它(a11y lint 会红),
    // 而每颗 chip 已各自收到 disabled —— 容器上再标一次本就是重复。
    <section
      data-video-purpose-row
      className={disabled ? "pointer-events-none opacity-60" : undefined}
    >
      <h2 className="mb-2 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-[13.5px] font-semibold text-foreground">拍这条片是为了啥</span>
        <span className="text-2xs font-normal text-muted-foreground">
          决定 AI 按哪套叙事弧编排 · 选定即锁
        </span>
      </h2>

      <OptionChipGroup label="视频目的">
        {/* 首位是「AI 判断」——不选不是空态,是一个说得出口的选项。 */}
        <OptionChip
          radio
          active={!current}
          tabIndex={!current ? 0 : -1}
          disabled={disabled}
          onClick={() => onChange(null)}
          title="不指定方向,AI 按你写的文案判该怎么拍"
          data-testid="purpose-auto"
        >
          <span className="inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" strokeWidth={2} />
            AI 判断
          </span>
        </OptionChip>
        {/* 四组卡口径:chip = 组(点组落组默认档);引擎契约仍是 8 值 label,组只是选择层。 */}
        {PURPOSE_GROUPS.map((g) => {
          const active = currentGroup?.key === g.key;
          return (
            <OptionChip
              key={g.key}
              radio
              active={active}
              tabIndex={active ? 0 : -1}
              disabled={disabled}
              onClick={() => onChange(g.defaultPurpose)}
              title={`${g.usage}(${g.members.join(" / ")})`}
              data-testid={`purpose-group-${g.key}`}
            >
              {g.title}
            </OptionChip>
          );
        })}
      </OptionChipGroup>

      <p className="mt-1.5 text-2xs text-muted-foreground">
        没选也能出片:AI 会按你的文案判该怎么拍。想指定方向就点上面一张 —— 选定后整条片按它编排,
        要换目的回视频舱重选。
      </p>
    </section>
  );
}
